import { useState, useCallback, useMemo, useEffect } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useNavigate } from "react-router-dom";
import { Edit, Search, Download, Upload } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import Layout from "../Layout";
import navigationRoutes from "../../utils/navigationRoutes";
import DynamicModuleService from "@/services/APIs/DynamicModuleService";
import { SchemaService } from "@/services/APIs";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { formatDate } from "../../utils/dateFormatter";
import { useQuery } from "@tanstack/react-query";

const PP_PropertyMaster: React.FC = () => {
  const navigate = useNavigate();

  const schemaName = "Property Master";
  const comparisonPropertySchemaName = "Compare Property";

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>("");

  // Get imported property IDs from localStorage (frontend-only tracking)
  const getImportedPropertyIds = useCallback((): Set<string> => {
    try {
      const stored = localStorage.getItem("importedPropertyIds");
      if (stored) {
        const ids = JSON.parse(stored);
        return new Set(Array.isArray(ids) ? ids : []);
      }
    } catch (error) {
      console.error(
        "Error reading imported property IDs from localStorage:",
        error
      );
    }
    return new Set<string>();
  }, []);

  const [importedPropertyIds, setImportedPropertyIds] = useState<Set<string>>(
    getImportedPropertyIds
  );

  // Fetch Comparison Property schema columns
  const { data: comparisonPropertyColumnData } = useQuery({
    queryKey: ["columnGridData", comparisonPropertySchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          comparisonPropertySchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!comparisonPropertySchemaName,
    retry: false,
  });

  // Fetch all comparison property data to calculate ARV
  const { data: comparisonPropertyData } = useQuery({
    queryKey: [
      "comparison-property-all",
      comparisonPropertySchemaName,
      comparisonPropertyColumnData,
    ],
    queryFn: async () => {
      if (!comparisonPropertyColumnData?.grid) {
        return { results: [], count: 0 };
      }
      const allColumns = comparisonPropertyColumnData.grid.map(
        (field: any) => field.field_name
      );
      if (!allColumns.map((col: string) => col.toLowerCase()).includes("id")) {
        allColumns.push("id");
      }
      if (
        !allColumns
          .map((col: string) => col.toLowerCase())
          .includes("propertymaster_id")
      ) {
        allColumns.push("propertymaster_id");
      }
      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          comparisonPropertySchemaName,
          1,
          10000,
          allColumns,
          undefined
        );
        if (response === undefined || response === null) {
          return { results: [], count: 0 };
        }
        if (Array.isArray(response)) {
          return { results: response, count: response.length };
        }
        return response ?? { results: [], count: 0 };
      } catch (error: any) {
        console.error("Error fetching comparison property:", error);
        return { results: [], count: 0 };
      }
    },
    enabled: !!comparisonPropertySchemaName && !!comparisonPropertyColumnData,
    retry: false,
  });

  // Define columns to fetch
  const columnsToFetch = useMemo(() => {
    return [
      "id",
      "caseno",
      "parcelid",
      "date",
      "propertyname",
      "lienamount",
      "bed",
      "bath",
      "sqfootage",
      "finalarv",
    ];
  }, []);

  // Fetch data
  const { data: moduleData, isLoading } = useQuery({
    queryKey: ["moduleData", schemaName, activeSearchTerm, columnsToFetch],
    queryFn: async () => {
      try {
        // Fetch more data when searching for client-side filtering
        const pageSize = activeSearchTerm ? 1000 : 1000;

        const result = await DynamicModuleService.getGridCoByConfig(
          schemaName,
          1,
          pageSize,
          columnsToFetch,
          undefined
        );

        let results: any[] = [];
        const responseAny = result as any;
        if (Array.isArray(result)) {
          results = result;
        } else if (responseAny?.results && Array.isArray(responseAny.results)) {
          results = responseAny.results;
        } else if (responseAny?.data && Array.isArray(responseAny.data)) {
          results = responseAny.data;
        }

        return results;
      } catch (error) {
        console.error("Error fetching properties:", error);
        return [];
      }
    },
    enabled: !!schemaName,
  });

  // Helper function to get field value (case-insensitive)
  const getFieldValue = (record: any, fieldName: string) => {
    const keys = Object.keys(record);
    const key = keys.find(k => k.toLowerCase() === fieldName.toLowerCase());
    return key ? record[key] : "";
  };

  // Create a map of propertyId -> comparison properties array
  const comparisonPropertiesByPropertyId = useMemo(() => {
    let comparisonProperties: any[] = [];
    if (Array.isArray(comparisonPropertyData)) {
      comparisonProperties = comparisonPropertyData;
    } else if (comparisonPropertyData?.results) {
      comparisonProperties = comparisonPropertyData.results;
    } else if (comparisonPropertyData?.data) {
      comparisonProperties = comparisonPropertyData.data;
    }

    const propertyToComparisonMap = new Map<string, any[]>();

    comparisonProperties.forEach((cp: any) => {
      const normalizedCp: any = {};
      Object.keys(cp).forEach(key => {
        normalizedCp[key.toLowerCase()] = cp[key];
      });

      const propertyId =
        normalizedCp.propertymaster_id ??
        normalizedCp.propertymasterid ??
        normalizedCp.property_master_id ??
        cp.propertymaster_id ??
        cp.propertymaster_Id ??
        cp.PropertyMasterId ??
        cp.propertymasterId ??
        cp.property_master_id ??
        cp.propertyMasterId ??
        cp.propertyMaster_ID;

      if (propertyId) {
        const idString = String(propertyId);
        if (!propertyToComparisonMap.has(idString)) {
          propertyToComparisonMap.set(idString, []);
        }
        propertyToComparisonMap.get(idString)!.push(cp);
      }
    });

    return propertyToComparisonMap;
  }, [comparisonPropertyData]);

  // Calculate Final ARV for a property based on its comparison properties
  const calculatePropertyARV = useCallback(
    (property: any, comparisonProperties: any[]): number | null => {
      if (!comparisonProperties || comparisonProperties.length === 0) {
        return null;
      }

      // Find persqftprice field in comparison properties (case-insensitive)
      const persqftpriceKey =
        comparisonProperties.length > 0
          ? Object.keys(comparisonProperties[0] ?? {}).find(
              key => key.toLowerCase() === "persqftprice"
            )
          : null;

      if (!persqftpriceKey) {
        return null;
      }

      // Calculate average PPSP from comparison properties
      const validPrices: number[] = [];
      comparisonProperties.forEach((cp: any) => {
        const value = cp[persqftpriceKey];
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (typeof numValue === "number" && !isNaN(numValue)) {
          validPrices.push(numValue);
        }
      });

      if (validPrices.length === 0) {
        return null;
      }

      const avgPPSP =
        validPrices.reduce((acc, price) => acc + price, 0) / validPrices.length;

      // Get sqfootage from property (case-insensitive)
      const sqfootageKey = Object.keys(property ?? {}).find(
        key => key.toLowerCase() === "sqfootage"
      );

      if (!sqfootageKey) {
        return null;
      }

      const sqfootageValue = property[sqfootageKey];
      const sqfootageNum =
        typeof sqfootageValue === "string"
          ? parseFloat(sqfootageValue)
          : sqfootageValue;

      if (
        typeof sqfootageNum !== "number" ||
        isNaN(sqfootageNum) ||
        sqfootageNum <= 0
      ) {
        return null;
      }

      // Calculate ARV = avgPPSP × sqfootage
      const arv = avgPPSP * sqfootageNum;
      return arv;
    },
    []
  );

  // Determine whether a record came from the import/upload flow (frontend-only check)
  const determineSource = useCallback(
    (record: any) => {
      if (!record || typeof record !== "object") {
        return "Manual";
      }

      // Get property ID
      const idRaw = record.id ?? record.ID ?? record.Id ?? record._id ?? null;
      const id = idRaw ? String(idRaw) : null;

      // Check if this property ID is in the imported list (frontend-only tracking)
      if (id && importedPropertyIds.has(id)) {
        return "Imported";
      }

      // Fallback: check for explicit source-like fields (if any exist in the data)
      const explicit =
        getFieldValue(record, "source") ??
        getFieldValue(record, "import_source") ??
        getFieldValue(record, "uploaded") ??
        getFieldValue(record, "imported");
      if (explicit) {
        const s = String(explicit).toLowerCase();
        if (s === "true" || s.includes("upload") || s.includes("import")) {
          return "Imported";
        }
        if (s.includes("manual")) {
          return "Manual";
        }
        return String(explicit);
      }

      return "Manual";
    },
    [importedPropertyIds]
  );

  // Filter data client-side based on search term
  const filteredData = useMemo(() => {
    if (!moduleData) {
      return [];
    }
    if (!activeSearchTerm) {
      return moduleData;
    }

    return moduleData.filter((item: any) => {
      const propertyName = getFieldValue(item, "propertyname");
      return propertyName
        .toLowerCase()
        .includes(activeSearchTerm.toLowerCase());
    });
  }, [moduleData, activeSearchTerm]);

  // Load imported property IDs from localStorage on mount and when moduleData changes
  useEffect(() => {
    const storedIds = getImportedPropertyIds();
    setImportedPropertyIds(storedIds);
  }, [moduleData, getImportedPropertyIds]);

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.EDIT_PROPERTY.replace(":id", id));
  };

  const handleExportToExcel = useCallback(async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      toast.error("Please select at least one property to export");
      return;
    }

    try {
      toast.loading("Exporting properties to Excel...", { id: "export-excel" });

      // Fetch all selected properties by their IDs
      const propertiesResponses = await Promise.all(
        selectedRowKeys.map(id =>
          DynamicModuleService.getItemById(schemaName, String(id))
        )
      );

      // Extract property data from responses (handle different response structures)
      const propertiesData = propertiesResponses.map((response: any) => {
        // Handle different response structures
        return response?.data ?? response ?? {};
      });

      // Define all columns to export with their display names
      const exportColumns = [
        { key: "caseno", label: "Case No" },
        { key: "parcelid", label: "Parcel ID" },
        { key: "date", label: "Date" },
        { key: "enddate", label: "End Date" },
        { key: "lienamount", label: "Lien Amount" },
        { key: "startingbid", label: "Starting Bid" },
        { key: "propertyname", label: "Property Name" },
        { key: "owners", label: "Owners" },
        { key: "persqftprice", label: "Per Sq Ft Price" },
        { key: "yearbuilt", label: "Year Built" },
        { key: "bed", label: "Bed" },
        { key: "bath", label: "Bath" },
        { key: "sqfootage", label: "Sq. Footage" },
        { key: "address", label: "Address" },
        { key: "rougharv", label: "Rough ARV" },
        { key: "finalarv", label: "Final ARV" },
        { key: "rehabestimate", label: "Rehab Estimate" },
        { key: "notes", label: "Notes" },
        { key: "photos", label: "Photos" },
        { key: "photos2", label: "Photos 2" },
        { key: "photos3", label: "Photos 3" },
        { key: "photos4", label: "Photos 4" },
        { key: "photos5", label: "Photos 5" },
        { key: "countysites", label: "County Sites" },
        { key: "countysites1", label: "County Sites 1" },
        { key: "countysites2", label: "County Sites 2" },
      ];

      // Prepare data for Excel
      const excelData = propertiesData.map((property: any) => {
        const row: any = {};
        exportColumns.forEach(col => {
          // Handle both lowercase and original case keys, and nested properties
          let value =
            property[col.key] ?? property[col.key.toLowerCase()] ?? "";

          // Handle array/object values (like photos)
          if (Array.isArray(value)) {
            value = value
              .map((item: any) => {
                if (typeof item === "object" && item !== null) {
                  return (
                    item.url ?? item.name ?? item.id ?? JSON.stringify(item)
                  );
                }
                return item;
              })
              .join(", ");
          } else if (typeof value === "object" && value !== null) {
            value =
              value.url ?? value.name ?? value.id ?? JSON.stringify(value);
          }

          row[col.label] = value ?? "";
        });
        return row;
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Properties");

      // Generate Excel file and download
      const fileName = `Properties_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success(
        `Successfully exported ${selectedRowKeys.length} property(ies) to Excel`,
        { id: "export-excel" }
      );
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error(
        error?.response?.data?.message ??
          "Failed to export properties to Excel",
        { id: "export-excel" }
      );
    }
  }, [selectedRowKeys, schemaName]);

  const handleSearch = useCallback(() => {
    setActiveSearchTerm(searchTerm.trim());
  }, [searchTerm]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setActiveSearchTerm("");
  }, []);

  // Handle row selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredData.map((item: any) => {
        const id = item.id ?? item.ID ?? item.Id ?? "";
        return String(id);
      });
      setSelectedRowKeys(allIds);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedRowKeys(prev => [...prev, id]);
    } else {
      setSelectedRowKeys(prev => prev.filter(key => key !== id));
    }
  };

  const isRowSelected = (id: string) => {
    return selectedRowKeys.includes(id);
  };

  const isAllSelected = useMemo(() => {
    if (filteredData.length === 0) {
      return false;
    }
    return filteredData.every((item: any) => {
      const id = String(item.id ?? item.ID ?? item.Id ?? "");
      return selectedRowKeys.includes(id);
    });
  }, [filteredData, selectedRowKeys]);

  return (
    <Layout>
      <div className="space-y-4 lg:space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          {/* Title always on first row */}
          <h1 className="w-1/2 text-2xl font-bold text-foreground lg:text-3xl">
            Property Master
          </h1>

          {/* Buttons container — force second row on LG */}
          <div className="flex flex-wrap items-center gap-3 lg:w-full lg:flex-row lg:justify-end">
            <Button
              variant="outline"
              className="border-[#4a5273] bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
              onClick={handleExportToExcel}
              disabled={!selectedRowKeys || selectedRowKeys.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Export Excel
            </Button>

            <Button
              variant="outline"
              className="border-[#4a5273] bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
              onClick={() => navigate(navigationRoutes.IMPORT_PROPERTY)}
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload
            </Button>

            <Button
              className="bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
              onClick={() => navigate(navigationRoutes.CREATE_PROPERTY)}
            >
              + Add Property
            </Button>
          </div>
        </div>

        <div className="flex max-w-2xl items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by property name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            />
          </div>
          <Button
            className="h-10 bg-[#4a5273] px-6 text-white hover:bg-[#4a5273]/90"
            onClick={handleSearch}
            disabled={!searchTerm.trim()}
          >
            <Search className="mr-2 h-4 w-4" />
            Search
          </Button>
          {activeSearchTerm && (
            <Button variant="outline" onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={e => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                    />
                  </TableHead>
                  <TableHead>NO.</TableHead>
                  <TableHead>CASE NO</TableHead>
                  <TableHead>PARCEL ID</TableHead>
                  <TableHead>DATE</TableHead>
                  <TableHead>PROPERTY NAME</TableHead>
                  <TableHead>LIEN AMOUNT</TableHead>
                  <TableHead>BED</TableHead>
                  <TableHead>BATH</TableHead>
                  <TableHead>SQ. FOOTAGE</TableHead>
                  <TableHead>FINAL ARV</TableHead>
                  <TableHead>SOURCE</TableHead>
                  <TableHead>ACTION</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={13} className="py-8 text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="py-8 text-center">
                      No properties found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((property: any, index: number) => {
                    const id = String(
                      property.id ?? property.ID ?? property.Id ?? ""
                    );
                    const caseNo = getFieldValue(property, "caseno");
                    const parcelId = getFieldValue(property, "parcelid");
                    const date = getFieldValue(property, "date");
                    const propertyName = getFieldValue(
                      property,
                      "propertyname"
                    );
                    const lienAmount = getFieldValue(property, "lienamount");
                    const bed = getFieldValue(property, "bed");
                    const bath = getFieldValue(property, "bath");
                    const sqFootage = getFieldValue(property, "sqfootage");
                    const finalArv = getFieldValue(property, "finalarv");
                    const isSelected = isRowSelected(id);

                    return (
                      <TableRow key={id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={e =>
                              handleSelectRow(id, e.target.checked)
                            }
                            className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                          />
                        </TableCell>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell className="font-medium">
                          {caseNo ?? "-"}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {parcelId ?? "-"}
                        </TableCell>
                        <TableCell>{date ? formatDate(date) : "-"}</TableCell>
                        <TableCell>{propertyName ?? "-"}</TableCell>
                        <TableCell>
                          {lienAmount
                            ? typeof lienAmount === "number"
                              ? lienAmount.toLocaleString()
                              : lienAmount
                            : "-"}
                        </TableCell>
                        <TableCell>{bed ?? "-"}</TableCell>
                        <TableCell>{bath ?? "-"}</TableCell>
                        <TableCell>{sqFootage ?? "-"}</TableCell>
                        <TableCell>
                          {(() => {
                            // Get comparison properties for this property
                            const comparisonProps =
                              comparisonPropertiesByPropertyId.get(id) ?? [];

                            // Calculate ARV from comparison properties
                            const calculatedARV = calculatePropertyARV(
                              property,
                              comparisonProps
                            );

                            // Use calculated ARV if available, otherwise use stored finalArv
                            const displayARV = calculatedARV ?? finalArv;

                            if (displayARV) {
                              return typeof displayARV === "number"
                                ? `$${displayARV.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                                : displayARV;
                            }
                            return "-";
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const srcLabel = determineSource(property);
                            const isImported = srcLabel === "Imported";
                            return (
                              <Badge
                                variant="outline"
                                className={
                                  isImported
                                    ? "border-emerald-200 bg-gradient-to-r from-green-50 to-emerald-50 font-medium text-emerald-700 shadow-sm"
                                    : "border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 font-medium text-purple-700 shadow-sm"
                                }
                              >
                                {srcLabel}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(id)}
                          >
                            <Edit className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PP_PropertyMaster;
